import { ChangeDetectionStrategy, Component, Injector } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { Store } from "@ngrx/store";
import {
  ClientSidePermissionPricingSchemeRoleLinkContainerModel,
  Pagination,
  PricingSchemeService,
  RolePricingSchemeLinkService,
  TenantRoleService,
} from "blg-akaun-ts-lib";
import moment from "moment";
import { RolePermSelectors } from "../../../../../../modules/permission/role-permissions-controller/selectors";
import { Observable, forkJoin, iif, of, zip } from "rxjs";
import { catchError, map, mergeMap } from "rxjs/operators";
import { SubSink } from "subsink2";
import { RolePricingSchemeLinkModel } from "../../../../../../models/advanced-search-models/role-pricing-scheme-link.model";
import { PermissionFacade } from "../../../../../../modules/permission/facades/permission.facade";
import { RolePricingSchemeLinkActions } from "../../../../../../modules/permission/role-pricing-scheme-link-controler/actions";
import { RolePricingSchemeLinkSelectors } from "../../../../../../modules/permission/role-pricing-scheme-link-controler/selectors";
import { SessionSelectors } from "../../../../../../modules/session/session-controller/selectors";
import { SessionStates } from "../../../../../../modules/session/session-controller/states";
import { ViewColumnComponent } from "../../../../../../view-column.component";
import { AppConfig } from "../../../../../../visa";
import { PermissionStates } from "../../../../permission-controller/root";
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: "app-pricing-scheme-link",
  templateUrl: "./pricing-scheme-link.component.html",
  styleUrls: ["./pricing-scheme-link.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore],
})
export class PricingSchemeLinkComponent extends ViewColumnComponent {
  protected compName = "Role Pricing Scheme Link Listing";
  protected readonly index = 2;
  private localState: LocalState;
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateAdd$ = this.componentStore.select(
    (state) => state.localState.deactivateAdd
  );
  readonly deactivateList$ = this.componentStore.select(
    (state) => state.localState.deactivateList
  );
  toggleColumn$: Observable<boolean>;
  private subs = new SubSink();
  readonly AppletLoginSubjectLink$ = this.sessionStore.select(
    SessionSelectors.selectAppletLoginSubjectLink
  );
  readonly userRank$ = this.sessionStore.select(
    SessionSelectors.selectUserRank
  );

  authToken = localStorage.getItem("authToken");
  appletGuid = sessionStorage.getItem("appletGuid");
  tenantCode = sessionStorage.getItem("tenantCode");

  searchModel = RolePricingSchemeLinkModel;

  defaultColDef = {
    filter: "agTextColumnFilter",
    floatingFilterComponentParams: { suppressFilterButton: true },
    minWidth: 200,
    flex: 2,
    sortable: true,
    resizable: true,
    suppressCsvExport: true,
  };

  gridApi;

  columnsDefs = [];
  tenantGuid: string;
  rank: string;
  roleGuid: string;

  //translation variable
  pricing_scheme_text: string;
  created_date_text: string;
  modified_date_text: string;
  create_button: string;

  constructor(
    private viewColFacade: PermissionFacade,
    private componentStore: ComponentStore<{ localState: LocalState }>,
    private rolePricingSchemeLinkService: RolePricingSchemeLinkService,
    private store: Store<PermissionStates>,
    private tenantRoleService: TenantRoleService,
    private pricingSchemeService: PricingSchemeService,
    private readonly sessionStore: Store<SessionStates>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.pricing_scheme_text = res.pricing_scheme;
        this.created_date_text = res.created_date;
        this.modified_date_text = res.modified_date;
        this.create_button = res.create_button_nocaps;
      });
    } else {
      this.pricing_scheme_text = 'Pricing Scheme';
      this.created_date_text = 'Created Date';
      this.modified_date_text = 'Modified Date';
      this.create_button = 'Create';
    }
    this.subs.sink = this.AppletLoginSubjectLink$.subscribe((session) => {
      if (session) {
        console.log("Session tenant guid: ", session);
        this.tenantGuid = session.tenant_guid.toString();
      }
    });

    this.subs.sink = this.userRank$.subscribe((rank) => {
      if (rank) {
        this.rank = rank;
      }
    });
  }

  ngOnInit(): void {
    this.columnsDefs = [
      // {
      //   headerName: "Role",
      //   field: "role",
      //   cellStyle: { textAlign: "left" },
      // },
      {
        headerName: this.pricing_scheme_text,
        field: "pricingScheme",
        cellStyle: { textAlign: "left" },
      },
      {
        headerName: this.created_date_text,
        field: "bl_applet_client_side_perm_to_role_link.created_date",
        headerClass: "text-center",
        valueFormatter: (params) => {
          let date = moment(params.value).format("YYYY-MM-DD HH:MM:ss");
          if (date === "Invalid date") {
            date = null;
          }
          return date;
        },
      },
      {
        headerName: this.modified_date_text,
        field: "app_perm_set_hdr.updated_date",
        headerClass: "text-center",
        valueFormatter: (params) => {
          let date = moment(params.value).format("YYYY-MM-DD HH:MM:ss");
          if (date === "Invalid date") {
            date = null;
          }
          return date;
        },
      },
    ];
    this.toggleColumn$ = this.viewColFacade.toggleColumn$;
    this.subs.sink = this.localState$.subscribe((a) => {
      this.localState = a;
      this.componentStore.setState({ localState: a });
    });

    this.subs.sink = this.store
      .select(RolePermSelectors.selectGuid)
      .subscribe((guid) => {
        if (guid) {
          this.roleGuid = guid;
        }
      });
  }

  onNext() {
    this.viewColFacade.updateInstance<LocalState>(this.index, {
      ...this.localState,
      deactivateAdd: true,
      deactivateList: false,
    });
    this.viewColFacade.onNextAndReset(this.index, 8);
  }

  onGridReady(params) {
    const apiVisa = AppConfig.apiVisa;
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();
    const datasource = {
      getRows: (grid) => {
        this.subs.sink = this.rolePricingSchemeLinkService
          .getRolePricingPermissionLinkByCriteria(
            new Pagination(grid.request.startRow, grid.request.endRow, [
              { columnName: "calcTotalRecords", operator: "=", value: "true" },
              {
                columnName: "applet_guid",
                operator: "=",
                value: this.appletGuid,
              },
              { columnName: "role_guid", operator: "=", value: this.roleGuid },
            ]),
            apiVisa,
            this.appletGuid,
            this.tenantGuid,
            this.rank
          )
          .pipe(
            mergeMap((b) => {
              const source: Observable<ClientSidePermissionPricingSchemeRoleLinkContainerModel>[] =
                [];
              b.data.forEach((doc) => {
                source.push(
                  zip(
                    doc.bl_applet_role_pricing_scheme_link.role_guid
                      ? this.tenantRoleService
                          .getByGuid(
                            doc.bl_applet_role_pricing_scheme_link.role_guid?.toString(),
                            AppConfig.apiVisa
                          )
                          .pipe(catchError((err) => of(err)))
                      : of(null),
                    doc.bl_applet_role_pricing_scheme_link.pricing_scheme_guid
                      ? this.pricingSchemeService
                          .getByGuid(
                            doc.bl_applet_role_pricing_scheme_link.pricing_scheme_guid?.toString(),
                            AppConfig.apiVisa
                          )
                          .pipe(catchError((err) => of(err)))
                      : of(null)
                  ).pipe(
                    map(([b_a, b_b]: any[]) => {
                      doc = Object.assign(
                        {
                          role: b_a ? b_a.data.app_mst_role?.name : "",
                        },
                        {
                          pricingScheme: b_b
                            ? b_b.data.bl_fi_mst_pricing_scheme_hdr?.name
                            : "",
                        },
                        doc
                      );
                      return doc;
                    })
                  )
                );
              });
              return iif(
                () => b.data.length > 0,
                forkJoin(source).pipe(
                  map((b_inner) => {
                    b.data = b_inner;
                    return b;
                  })
                ),
                of(b)
              );
            })
          )
          .subscribe(
            (a) => {
              console.log("a.data", a.data);
              grid.success({
                rowData: a.data,
                rowCount: a.totalRecords,
              });
            },
            (err) => {
              grid.fail();
            }
          );
      },
    };
    this.gridApi.setServerSideDatasource(datasource);
    this.subs.sink = this.store
      .select(RolePricingSchemeLinkSelectors.selectRequiresUpdate)
      .subscribe((resolved) => {
        if (resolved) {
          this.gridApi.refreshServerSideStore();
          this.store.dispatch(RolePricingSchemeLinkActions.resetAgGrid());
        }
      });
  }

  onToggle(e: boolean) {
    this.viewColFacade.toggleColumn(e);
  }

  onRowClicked(entity: any) {
    this.store.dispatch(
      RolePricingSchemeLinkActions.selectRolePricingSchemeLink({
        RolePricingSchemeLink: entity,
      })
    );
    if (!this.localState.deactivateList) {
      this.viewColFacade.updateInstance<LocalState>(this.index, {
        ...this.localState,
        deactivateAdd: false,
        deactivateList: true,
      });
      this.viewColFacade.onNextAndReset(this.index, 9);
    }
  }
}
